import React, { useEffect, useRef, useState } from 'react';
import type { EinweisungGruppe } from '../../sige-plan/types';
import { MINDESTLESEZEIT_MS, darfWeiterNachLesen, istScrollEnde, restSekunden } from './einweisungTeilnahmeLogic';
import { HINWEISKASTEN, KNOPF_PRIMAER, KNOPF_SEKUNDAER } from '../../sifa/public/teilnahmeSeiteStil';

const AUSHANG_HINWEIS =
  'Der vollständige SiGe-Plan hängt auf der Baustelle aus bzw. liegt beim SiGe-Koordinator.';

/**
 * Alle Gruppen des Blattes mit ihren Stichpunkten auf EINER scrollbaren
 * Seite (das Blatt ist hoechstens zwei Seiten lang). „Weiter" wird frei,
 * sobald bis zum Ende gescrollt wurde ODER die Mindestlesezeit um ist
 * (darfWeiterNachLesen). Gescrollt wird das Fenster, nicht ein Kasten —
 * auf dem Handy ist ein Scrollkasten im Scrollkasten eine Falle.
 *
 * Der Aufrufer setzt `key`, damit Zeit und Freigabe je Person neu beginnen
 * (naechste Person → Schritt „inhalt").
 */
export const EinweisungInhalt: React.FC<{
  gruppen: EinweisungGruppe[];
  onWeiter: () => void;
  onZurueck: () => void;
}> = ({ gruppen, onWeiter, onZurueck }) => {
  const [frei, setFrei] = useState(false);
  const [rest, setRest] = useState(Math.ceil(MINDESTLESEZEIT_MS / 1000));
  // Startzeitpunkt erst im Effekt setzen — im Render waere Date.now() unrein.
  const start = useRef(0);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    start.current = Date.now();
    const pruefen = () => {
      const doc = document.documentElement;
      const scrollEnde = istScrollEnde(window.scrollY, window.innerHeight, doc.scrollHeight);
      const msGelesen = Date.now() - start.current;
      setRest(restSekunden(msGelesen));
      if (darfWeiterNachLesen(scrollEnde, msGelesen)) setFrei(true);
    };
    const takt = setInterval(pruefen, 500);
    window.addEventListener('scroll', pruefen, { passive: true });
    window.addEventListener('resize', pruefen);
    pruefen();
    return () => {
      clearInterval(takt);
      window.removeEventListener('scroll', pruefen);
      window.removeEventListener('resize', pruefen);
    };
  }, []);

  const sichtbareGruppen = gruppen.filter((g) => g.punkte.length > 0);

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">Inhalt der Einweisung</h2>
        <p className="text-slate-600">Bitte lies alle Punkte aufmerksam durch.</p>
      </div>

      {sichtbareGruppen.length === 0 ? (
        <div className={HINWEISKASTEN}>Dieses Blatt enthält keine Stichpunkte.</div>
      ) : (
        <div className="space-y-5">
          {sichtbareGruppen.map((g) => (
            <section key={g.id} className="space-y-2">
              <h3 className="text-lg font-semibold text-slate-900">{g.titel}</h3>
              <ul className="list-disc pl-6 space-y-1.5 leading-relaxed text-slate-800">
                {g.punkte.map((p, i) => (
                  <li key={i} className="whitespace-pre-line">
                    {p}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <div className={HINWEISKASTEN}>{AUSHANG_HINWEIS}</div>

      {!frei && (
        <div className={HINWEISKASTEN} aria-live="polite">
          Bitte bis zum Ende lesen{rest > 0 ? ` — „Weiter" ist in ${rest} s frei.` : '.'}
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} className={KNOPF_SEKUNDAER}>
          Zurück
        </button>
        <button type="button" onClick={onWeiter} disabled={!frei} className={KNOPF_PRIMAER}>
          {frei || rest <= 0 ? 'Weiter' : `Weiter (noch ${rest} s)`}
        </button>
      </div>
    </div>
  );
};
